/**
 * <FocusRing> — l'anneau de focus jaune (atom, Brand core V13 chap. 6).
 *
 * « Focus = anneau 2 px jaune décalé de 2 px. » C'est le seul endroit du DS où le
 * jaune signature entoure une surface, et c'est aussi le seul endroit où cette géométrie est
 * écrite : `<Button>`, `<IconButton>` et `<TextButton>` la partagent au lieu de la refaire —
 * sinon trois boutons dérivent chacun de leur côté.
 *
 * Purement décoratif : il se pose en absolu par-dessus le parent, hors flux, et ne prend jamais
 * le pointeur. Le parent reste responsable de savoir s'il a le focus (`<Pressable>` donne
 * `focused` sur le web ; sur mobile tactile il n'y a pas de focus clavier, l'anneau ne sort pas).
 *
 * **Il ne sort qu'au clavier**, comme `:focus-visible` au web (Wajih, 20/10 : le contour restait sur
 * un bouton cliqué) : sur le web, `focused` vient de tout focus, celui d'un clic compris, et c'est
 * l'anneau qui demande au navigateur si ce focus se montre (`useFocusVisible`). Une seule règle, ici,
 * pour tous les boutons qui le posent.
 *
 * `radius` est celui du composant entouré : l'anneau prend ce rayon plus son propre décalage,
 * pour rester concentrique. Géométrie intrinsèque, pas des jetons (cf. contrat §2).
 */
import React from "react"
import { Platform, View, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useFocusVisible } from "./useFocusVisible"

/** Écart entre la surface et l'anneau, puis épaisseur de l'anneau. */
const GAP = 2
const THICKNESS = 2

/**
 * À appliquer sur la surface qui porte l'anneau. Le navigateur dessine son propre anneau de
 * focus, qui viendrait doubler le nôtre ; on ne l'éteint donc que là où le nôtre prend le relais,
 * jamais ailleurs — un focus invisible serait pire que deux anneaux.
 */
export const focusRingHost: ViewStyle | undefined = Platform.OS === "web" ? { outlineWidth: 0 } : undefined

export type FocusRingProps = {
    /**
     * Le parent a le focus. Rien n'est rendu tant que c'est faux ; vrai, l'anneau ne sort que si le
     * focus se montre (le clavier, pas un clic).
     */
    visible: boolean
    /** Rayon de la surface entourée, en px. */
    radius: number
    testID?: string
}

export function FocusRing(props: FocusRingProps) {
    const { visible, radius, testID } = props
    const shown = useFocusVisible(visible)

    if (!shown) return null

    return <View testID={testID} pointerEvents="none" style={styles.ring(radius)} />
}

const styles = StyleSheet.create((theme) => ({
    ring: (radius: number) => {
        const inset = -(GAP + THICKNESS)
        return {
            position: "absolute" as const,
            top: inset,
            left: inset,
            right: inset,
            bottom: inset,
            borderWidth: THICKNESS,
            borderColor: theme.colors.accent,
            borderRadius: radius + GAP + THICKNESS,
        }
    },
}))
